import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAgentOverride } from '@/hooks/useAgentOverride';
import { useAuth } from '@/hooks/useAuth';
import { AgentBanner } from '@/components/shared/AgentBanner';
import { StepIndicator } from '@/components/shared/StepIndicator';
import { BottomNav } from '@/components/layout/BottomNav';
import { useStore } from '@/store';

const TIER_BADGE: Record<string, string> = {
  Bronze:   'text-amber-300 bg-amber-900/40 border border-amber-700/50',
  Silver:   'text-gray-300 bg-gray-700/40 border border-gray-600/50',
  Gold:     'text-yellow-300 bg-yellow-900/40 border border-yellow-700/50',
  Platinum: 'text-brand-300 bg-brand-900/40 border border-brand-700/50',
};

export function MobileShell() {
  useAgentOverride();
  const { handleSignOut } = useAuth();
  const currentStep = useStore((s) => s.currentStep);
  const isSyncing = useStore((s) => s.isSyncing);
  const isAuthenticated = useStore((s) => s.isAuthenticated);
  const userName = useStore((s) => s.userName);
  const loyaltyTier = useStore((s) => s.loyaltyTier);
  const navigate = useNavigate();
  const location = useLocation();
  const hideStepIndicator = ['/confirmation', '/profile', '/my-flights', '/contact', '/admin']
    .some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  const tierColor = loyaltyTier ? (TIER_BADGE[loyaltyTier] ?? TIER_BADGE.Silver) : '';

  return (
    <div className="flex flex-col min-h-screen bg-app">
      {/* Header — Pronetx dark frame */}
      <header className="bg-frame px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <button
          onClick={() => navigate('/')}
          aria-label="Pronetx Airlines — Home"
          className="flex items-center gap-2 focus-ring rounded"
        >
          <img
            src="https://cdn.prod.website-files.com/69148a80ebab6c41c3621a67/693ba81b001c674edfb758d7_LOGO-PRONETX-Horiz-Black-2-tone-001%202.png"
            alt=""
            width={80}
            height={20}
            className="h-5 w-auto object-contain invert brightness-90"
          />
          <span className="text-[10px] font-semibold text-brand-300 tracking-[0.08em] uppercase">
            Airlines
          </span>
        </button>

        <div className="flex items-center gap-2">
          {isSyncing && (
            <span className="text-brand-300 text-xs animate-spin inline-block" aria-label="Syncing">↻</span>
          )}

          {isAuthenticated ? (
            <div className="flex items-center gap-2">
              {loyaltyTier && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${tierColor}`}>
                  {loyaltyTier}
                </span>
              )}
              <span className="text-xs text-white/80 font-medium max-w-[80px] truncate">
                {userName?.split(' ')[0] ?? 'Account'}
              </span>
              <button
                onClick={async () => { await handleSignOut(); navigate('/login'); }}
                aria-label="Sign out"
                className="text-white/60 hover:text-white transition-colors p-1 focus-ring rounded"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
                  className="w-4 h-4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="text-xs bg-brand-500 hover:bg-brand-600 text-white font-semibold px-3 py-1.5 rounded-full transition-colors focus-ring"
            >
              Sign in
            </button>
          )}
        </div>
      </header>

      <AgentBanner />

      {!hideStepIndicator && <StepIndicator currentStep={currentStep} />}

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      <BottomNav />
    </div>
  );
}
